import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';

import { GAME_MODE, HighScoreEntity, SingSetup } from '~/interfaces';
import CameraManager from '~/modules/camera/camera-manager';
import { Button } from '~/modules/elements/akui/button';
import styles from '~/modules/game-engine/drawing/styles';
import { sumDetailedScore } from '~/modules/game-engine/game-state/helpers/calculate-score';
import useKeyboardNav, { RegisterFunc } from '~/modules/hooks/use-keyboard-nav';
import { gradeOf } from '~/modules/user-profiles/profile-stats';
import { PlayerScore } from '~/routes/game/singing/post-game/post-game-view';
import { useNextUp } from '~/routes/game/singing/post-game/views/next-up';
import { NextUpLabel } from '~/routes/game/singing/post-game/views/next-up-label';
import CameraRoll from '~/routes/game/singing/post-game/views/results/camera-roll';
import { CameraRollPlaceholder } from '~/routes/game/singing/post-game/views/results/camera-roll-placeholder';
import { rankByScore } from '~/routes/game/singing/post-game/views/results/ranking';
import {
  countUpScore,
  isCountUpDone,
  RESULTS_REVEAL_MS,
  revealedAt,
} from '~/routes/game/singing/post-game/views/results/reveal-timeline';
import SingerPanel from '~/routes/game/singing/post-game/views/results/singer-panel';

interface Props {
  onNextStep: () => void;
  players: PlayerScore[];
  highScores: HighScoreEntity[];
  singSetup: SingSetup;
  /** Online mode hides the camera roll — the singers are not in the same room. */
  cameraEnabled?: boolean;
  /** Whether leaving here plays the running order, which is what the primary can then announce. */
  queueAdvances?: boolean;
}

/**
 * How the panels share the width. One or two sit side by side; three or four fall into a 2x2 grid
 * rather than a row of four, which at 460px of timelapse beside them leaves each singer a column too
 * narrow for the score to read from a sofa. Five to eight go three or four across, two deep, as dense panels.
 */
const PANEL_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-2',
  4: 'lg:grid-cols-2',
  5: 'lg:grid-cols-3',
  6: 'lg:grid-cols-3',
};

/** From here the panels are dense: half the width a 2x2 panel gets or less, so they shed their words. */
const DENSE_FROM_PLAYERS = 5;

/**
 * Milliseconds since the results appeared, and a way to jump to the end of them.
 *
 * A frame clock rather than a step counter: the score counts up continuously over 1.6s and the three
 * reveals after it are timed to the millisecond, which four `setInterval` steps cannot express. It
 * stops itself once everything has arrived, so nothing is still ticking while the screen is read.
 */
function useResultsClock() {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (elapsed >= RESULTS_REVEAL_MS) return;

    const started = performance.now() - elapsed;
    let frame = requestAnimationFrame(function tick(now) {
      const next = Math.min(RESULTS_REVEAL_MS, now - started);
      setElapsed(next);
      if (next < RESULTS_REVEAL_MS) frame = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frame);
    // Restarting on `elapsed` would reset the clock every frame; it is read once to resume after a
    // skip has set it, and the effect is otherwise driven by the loop it starts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed >= RESULTS_REVEAL_MS]);

  return { elapsed, finish: () => setElapsed(RESULTS_REVEAL_MS) };
}

function ResultsView({
  onNextStep,
  players,
  highScores,
  singSetup,
  cameraEnabled = true,
  queueAdvances = true,
}: Props) {
  const { elapsed, finish } = useResultsClock();

  // The primary stops being "skip" the moment the score has landed, as the frame's timeline does -
  // the reveals after it are short enough that nobody waits on them.
  const isAnimFinished = isCountUpDone(elapsed);
  const reveal = revealedAt(elapsed);

  const nextStep = () => {
    if (!isAnimFinished) {
      finish();
    } else {
      onNextStep();
    }
  };

  const isCoop = singSetup.mode === GAME_MODE.CO_OP;
  const finalPlayers = isCoop ? [{ ...players[0], name: players.map((player) => player.name).join(', ') }] : players;

  // Co-op is one team panel wearing everybody's colours; every other mode is one panel per singer.
  const coopColours = players.map((player) => styles.colors.players[player.playerNumber].text);

  const finalScores = finalPlayers.map((player) => sumDetailedScore(player.detailedScore[0]));
  const ranking = rankByScore(finalScores);

  const isHighScore = (playerName: string) =>
    highScores.some((score) => score.singSetupId === singSetup.id && score.name === playerName);

  const compact = finalPlayers.length > 2;

  // Registered after the scores it reports, so the subtitle can carry them. Everything else on this
  // screen - the panels, the count-up, the grade badge, the camera roll - is a read, so one line is
  // the whole of what a phone can be told about it.
  const { register } = useKeyboardNav({
    title: 'Your score',
    primary: 'next-button',
    // Settles once, when the scores are known, rather than following the count-up: the animation is
    // a television flourish and the phone does not narrate it.
    subtitle: finalPlayers
      .map((player, index) => {
        const place = finalPlayers.length > 1 ? ` \u00b7 ${ranking[index]}` : '';
        return `${player.name} \u00b7 ${finalScores[index].toLocaleString()} \u00b7 ${gradeOf({ score: finalScores[index], notes: player.notes })}${place}`;
      })
      .join('   '),
    onTelevision: true,
    presentation: 'strip',
  });

  const initialCameraPermission = useMemo(() => CameraManager.getPermissionStatus(), []);
  // needs to be here to force rerender of Results so Next button is selected after enabling camera
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const enableCamera = () => {
    setIsRequestInProgress(true);
    CameraManager.requestPermissions().then(() => setIsRequestInProgress(false));
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-5">
        <div
          className={clsx(
            'grid min-h-0 min-w-0 flex-1 gap-4 lg:gap-5',
            PANEL_COLUMNS[finalPlayers.length] ?? 'lg:grid-cols-4',
            // One row of panels is as tall as its content, as the frame packs it. Two rows share the
            // height instead: left to size themselves the second row is as tall as it likes and runs
            // off the bottom of a 1080 screen, painting over the tip underneath.
            compact ? 'lg:auto-rows-[minmax(0,1fr)]' : 'lg:auto-rows-min',
          )}>
          {finalPlayers.map((player, index) => (
            <SingerPanel
              key={player.playerNumber}
              player={player}
              playerNumber={player.playerNumber}
              score={countUpScore(player.detailedScore[0], elapsed)}
              grade={gradeOf({ score: finalScores[index], notes: player.notes })}
              colours={isCoop ? coopColours : [styles.colors.players[player.playerNumber].text]}
              rank={finalPlayers.length > 1 ? ranking[index] : undefined}
              isHighScore={reveal.grade && isHighScore(player.name)}
              isCoop={isCoop}
              series={player.accuracyOverTime}
              compact={compact}
              dense={finalPlayers.length >= DENSE_FROM_PLAYERS}
              // A lone panel stops at the frame's 860px rather than sprawling the full width beside
              // a 460px timelapse - a score card three feet wide reads as a mistake.
              className={finalPlayers.length === 1 ? 'lg:max-w-[53.75rem]' : undefined}
              revealGrade={reveal.grade}
              revealStats={reveal.stats}
              revealJudgements={reveal.judgements}
              revealGraph={reveal.graph}
            />
          ))}
        </div>
        {cameraEnabled && (
          <div className="flex flex-col gap-3.5 lg:w-[28.75rem] lg:shrink-0">
            {initialCameraPermission ? (
              <CameraRoll />
            ) : (
              <CameraRollPlaceholder register={register} onConfirm={enableCamera} loading={isRequestInProgress} />
            )}
            <NextButton
              register={register}
              isAnimFinished={isAnimFinished}
              onClick={nextStep}
              queueAdvances={queueAdvances}
            />
          </div>
        )}
      </div>
      {!cameraEnabled && (
        <NextButton
          register={register}
          isAnimFinished={isAnimFinished}
          onClick={nextStep}
          queueAdvances={queueAdvances}
        />
      )}
    </>
  );
}

/**
 * Its own component so `register` runs during THIS render rather than the parent's: a register()
 * call inline in the parent's JSX runs before any child renders, which would place this button ahead
 * of the camera-roll placeholder above it — both in arrow navigation and in the control list
 * mirrored to the remote mic, neither of which would then match what's on screen.
 */
export function NextButton({
  register,
  isAnimFinished,
  onClick,
  queueAdvances,
}: {
  register: RegisterFunc;
  isAnimFinished: boolean;
  onClick: () => void;
  queueAdvances: boolean;
}) {
  // Only once the score has landed: while the button still means "skip", a countdown beside the word
  // Skip would be counting down to something else entirely.
  const { next, secondsLeft } = useNextUp(queueAdvances && isAnimFinished, onClick);

  const showsNextUp = queueAdvances && isAnimFinished && next !== undefined;
  const label = isAnimFinished ? 'Next' : 'Skip';

  return (
    <Button
      {...register('next-button', onClick, undefined, true, {
        // Held at `Next` for the whole life of the screen, where the television's own label flips
        // from `Skip` to `Next` the moment the count-up lands. The two words are the same button one
        // second apart, and a thumb already moving towards `Skip` that arrives after the flip leaves
        // a screen nobody meant to leave. Pressing this early still skips the animation - it just
        // never changes meaning under the finger. The television keeps both words: there the
        // animation is on screen, so `Skip` is naming something you can see.
        control: { type: 'button', label: 'Next' },
      })}
      data-test={isAnimFinished ? 'highscores-button' : 'skip-animation-button'}
      size="small"
      className="mt-auto h-[4.375rem] w-full px-4 text-xl">
      {showsNextUp ? <NextUpLabel next={next} secondsLeft={secondsLeft} /> : label}
    </Button>
  );
}

export default ResultsView;
